import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { clsx } from 'clsx';
import type { DashboardControl } from '@/types/dashboard';
import { TIME_RANGE_PRESETS, getPresetByKey } from './time-range-presets';

interface TimeRangeControlProps {
  control: DashboardControl;
  currentValue: unknown;
  onChange: (parameterKey: string, value: unknown) => void;
}

const TimeRangeControl: React.FC<TimeRangeControlProps> = ({
  control,
  currentValue,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const configPresets = (control.config.presets as string[] | undefined) ?? [];
  const defaultPreset = control.config.default as string | undefined;

  const availablePresets =
    configPresets.length > 0
      ? TIME_RANGE_PRESETS.filter((p) => configPresets.includes(p.key))
      : TIME_RANGE_PRESETS;

  const selectedKey = currentValue
    ? findMatchingPresetKey(currentValue)
    : defaultPreset ?? null;

  const selectedLabel =
    selectedKey
      ? getPresetByKey(selectedKey)?.label ?? control.label
      : control.label;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (presetKey: string) => {
    const preset = getPresetByKey(presetKey);
    if (preset) {
      onChange(control.parameter_key, preset.getConfig());
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen((v) => !v)}
        className={clsx(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
          'border border-border',
          'bg-card',
          'text-foreground',
          'hover:bg-accent',
          isOpen && 'ring-2 ring-primary/40'
        )}
      >
        <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
        <span>{selectedLabel}</span>
        <ChevronDown
          className={clsx(
            'w-3.5 h-3.5 text-muted-foreground transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 z-50 min-w-[180px] rounded-lg border border-border bg-card shadow-lg py-1">
          {availablePresets.map((preset) => (
            <button
              key={preset.key}
              onClick={() => handleSelect(preset.key)}
              className={clsx(
                'w-full text-left px-3 py-2 text-sm transition-colors',
                selectedKey === preset.key
                  ? 'bg-accent text-primary font-medium'
                  : 'text-foreground hover:bg-accent'
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function findMatchingPresetKey(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;

  for (const preset of TIME_RANGE_PRESETS) {
    const config = preset.getConfig();
    if (config.type === v.type && config.value === v.value) {
      if (config.type !== 'specific_range') return preset.key;
    }
  }
  return null;
}

export default TimeRangeControl;
